import React from "react"
/* eslint-disable react/no-array-index-key */
import { useInView } from "react-intersection-observer"
import { css, logTool } from "@jsUtils"

import PageCircleItem from "./PageCircleItem"
import Styles from "./pagecircles.module.scss"

const log = logTool(true)

// HEIGHT OF THE CIRCLE WRAPPER
const CIRCLE = {
  BASE: 1000,
  RATIO: 0.5,
  MULTIPLIER: 1 // only if we need to tweak, which at present, does not seem like we do
}

/**
 * Create an array of PageCircleItems
 * @param {array|object} items - array of settings objects for generating PageCircleItem components
 * @param {object} hooks - react hooks from useCreateInView custom hook
 * @returns {array} - react nodes as an array (of PageCircleItem components)
 */
const makeCircles = (items, hooks) => {
  const els = Array.isArray(items) ? items : [items]

  const circleSet = els.map((val, i) => {
    return (
      <PageCircleItem
        alignment={val.align}
        color={val.color}
        fromTop={val.fromTop}
        orbit={val.orbit}
        size={val.size}
        hideRing={val.hideRing || false}
        extraClasses={val.extraClasses}
        // key must be consistent for the react hooks + refs to keep working
        // hence suppression of the array index lint warning
        key={`circle-${i}`}
        ref={hooks[i][0]}
        ready={css(
          hooks[i][1] && Styles.ready,
          hooks[i][1] && val.extraClasses && val.extraClasses.ready
        )}
      />
    )
  })
  return circleSet
}

/**
 * Take a multiplier time a base, based on our number of circles.
 * @param  {object|array} circles - array or object to get length of the set
 * @return {string}         CSS units for the height of the block, based on the number of circles and our constants defined above
 *
 * We want a height that is a multiple of how many we have, tempered by the RATIO per extra item
 */
const getCircleHeight = circles => {
  const len = Array.isArray(circles) ? circles.length : 1
  const { BASE, RATIO, MULTIPLIER } = CIRCLE
  const output = len * MULTIPLIER * BASE + BASE * RATIO * len
  return output
}

/**
 * PageCircles creates a single or a set of PageCircleItem components
 * @param {object} props - react props for creation of PageCircleItem components
 * @param {object} props.settings - object or object with an array of objects meeting the following configuration:
 *  {
 *    align {string}
 *    color {string}
 *    fromTop {string}
 *    orbit {boolean}
 *    size {string}
 *    extraClasses {object}
 *  }
 * @param {string} props.relativeParent - is this absolutely positioned or an instance absolute to a relative parent. Remeber this DOES NOT mean the instance is relative, it means the PARENT is relative
 */
const PageCircles = props => {
  const { settings, relativeParent, className } = props
  /**
   * Custom hook to create hooks for each useInView instance related to each PageCircleItem
   * @param {array|object} i - array of settings.items, or a single settings item object; each item a PageCircleItem component
   * @returns {array} - array of hooks arrays for useInView (multidimensional array of arrays, as each
   * returned value from useInView is an array)
   * As noted below, these must be distinct from the component generation in
   * makeCircles() because their identity needs to be beyond the life span of the components themselves.
   */
  const [cirRef, cirView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  })
  const useCreateInView = i => {
    const views = []
    const els = Array.isArray(i) ? i : [i]
    els.forEach(() => {
      views.push([cirRef, cirView])
    })
    return views
  }

  // NOTE: can't use the hooks inside the makeCircles or React loses the connection to the
  // hooks status and refs; so the generation of the hooks and the creation of child react items
  // with the forwardRef needs to be distinct
  const viewers = useCreateInView(settings.items || settings)
  const circleSet = makeCircles(settings.items || settings, viewers)

  const height = `${getCircleHeight(circleSet)}px`

  const relAttr = {
    ...(relativeParent && {
      top: relativeParent
    })
  }
  const styAttr = {
    ...relAttr,
    minHeight: height
  }

  return (
    <div className={css(Styles.circleSet, className)} style={styAttr}>
      {circleSet}
    </div>
  )
}

export default PageCircles
